
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import Modal from 'components/partials/modal/modal';
import { TextArea } from 'ui/text-area';

import SpecialAwardPopulationFields from './special-award-population-fields';

import { SPECIAL_AWARD_DESCRIPTION_MAX_LENGTH } from '../calculations-step.constants';
import { useSpecialAwardForm } from '../hooks/use-special-award-form';
import { useSpecialAwardModalData } from '../hooks/use-special-award-modal-data';
import { useSubmitSpecialAward } from '../hooks/use-submit-special-award';

import type { SpecialAwardRow } from '../calculations-step.types';
import type {
  AidModelCalculationDto,
  AidModelCalculationRequest,
} from 'api/generated/schemas';

export interface AddSpecialAwardModalProps {
  userId: string;
  modelId: string | number;
  clientId: string | number;
  calculation?: AidModelCalculationDto;
  settingsToRequest: () => AidModelCalculationRequest;
  rule?: SpecialAwardRow;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddSpecialAwardModal = ({
  userId,
  modelId,
  clientId,
  calculation,
  settingsToRequest,
  rule,
  onClose,
  onSuccess,
}: AddSpecialAwardModalProps) => {
  const data = useSpecialAwardModalData({ userId, modelId, clientId });

  const form = useSpecialAwardForm({
    regimes: data.regimes,
    customLogicCategories: data.customLogicCategories,
    initialRule: rule,
    isPrefillReady: data.isPrefillReady,
  });

  const { submit, canSubmit, isSubmitting } = useSubmitSpecialAward({
    userId,
    modelId,
    calculation,
    settingsToRequest,
    rule,
    defaultAwardLogic: data.defaultAwardLogic,
    isValid: form.isValid,
    toRequests: form.toRequests,
    onClose,
    onSuccess,
  });

  const isEditing = rule?.id != null;

  const footer = (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
      <Button
        type="button"
        variant="brand-filled"
        size="md"
        disabled={!canSubmit}
        loading={isSubmitting}
        onClick={submit}
        data-testid="submit-special-award-button"
      >
        {isEditing ? 'Save' : 'Add'}
      </Button>
      <Button
        type="button"
        variant="brand-outlined"
        size="md"
        onClick={onClose}
        data-testid="cancel-special-award-button"
      >
        Cancel
      </Button>
    </Stack>
  );

  return (
    <Modal
      headerText={isEditing ? 'Edit special award' : 'Add special award'}
      onCloseCallback={onClose}
      showCloseButton
      disableBackdropClick
      skipUnmountOnClose
      footer={footer}
      primaryAction={{
        onClick: submit,
        disabled: !canSubmit,
        loading: isSubmitting,
      }}
      modalClassName="!w-[640px] max-w-[92vw] !overflow-visible p-5"
      headerClassName="text-2xl font-bold"
      data-testid="add-special-award-modal"
    >
      <Stack sx={{ mt: 2, gap: 2.5 }}>
        <SpecialAwardPopulationFields form={form} data={data} />

        <TextArea
          label="Description"
          id="special-award-description"
          rows={3}
          showCharCount={false}
          maxLength={SPECIAL_AWARD_DESCRIPTION_MAX_LENGTH}
          value={form.description}
          onChange={form.setDescription}
          resize="none"
          data-testid="special-award-description"
        />
      </Stack>
    </Modal>
  );
};

export default AddSpecialAwardModal;
